import { universalStorage } from "@/utils/universal-storage";

export interface LanguageConfig {
  name: string;
  ietf: string;
  unicodeBCP47: string;
}

export const languages = [
  {
    name: "English",
    ietf: "en",
    unicodeBCP47: "en-US",
  },
  {
    name: "German",
    ietf: "de",
    unicodeBCP47: "de-DE",
  },
  {
    name: "Spanish",
    ietf: "es",
    unicodeBCP47: "es-ES",
  },
  {
    name: "French",
    ietf: "fr",
    unicodeBCP47: "fr-FR",
  },
  {
    name: "Portuguese",
    ietf: "pt",
    unicodeBCP47: "pt-BR",
  },
  {
    name: "Japanese",
    ietf: "ja",
    unicodeBCP47: "ja-JP",
  },
  {
    name: "Korean",
    ietf: "ko",
    unicodeBCP47: "ko-KR",
  },
] as const satisfies ReadonlyArray<LanguageConfig>;

export type Language = (typeof languages)[number];
export type UnicodeBCP47 = Language["unicodeBCP47"];
export type IETF = Language["ietf"];

/**
 * The default language code to use when no language is selected
 */
export const defaultLangCode: IETF = "en";

/**
 * Gets the language object for a given IETF language code
 * @param ietf - The IETF language code to look up
 * @returns The matching Language object
 */
export const getLanguage = (ietf: IETF): Language => {
  return languages.find((lang) => lang.ietf === ietf) ?? languages[0];
};

/**
 * Gets the Unicode BCP47 language code for the currently selected language
 * @returns The Unicode BCP47 code
 */
export const getUnicodeBCP47 = (): UnicodeBCP47 => {
  const storedLangCode = universalStorage.get("lang");
  return getLanguage(storedLangCode ?? defaultLangCode).unicodeBCP47;
};

/**
 * Gets the IETF language code for the currently selected language
 * @returns The IETF code
 */
export const getIETF = (): IETF => {
  const storedLangCode = universalStorage.get("lang");
  return getLanguage(storedLangCode ?? defaultLangCode).ietf;
};
